import { useEffect, useMemo } from "react";
import { useLocalStorage } from "usehooks-ts";

const STORAGE_KEY = "selectedTeamName";

/**
 * Returns [selectedTeam, setStoredTeam].
 * selectedTeam is the stored name if it exists in the provided teams list,
 * otherwise falls back to the first team in the list.
 * setStoredTeam persists the selection to localStorage.
 *
 * @param teams - The list of available teams (should be pre-sorted)
 */
export function useStoredTeam(teams: string[]): [string, (team: string) => void] {
    const [storedTeam, setStoredTeam] = useLocalStorage<string>(STORAGE_KEY, "");

    // Calculate the selected team
    const selectedTeam = useMemo(() => {
        // If we have teams loaded
        if (teams && teams.length > 0) {
            // Use stored team if it's in the list
            if (storedTeam && teams.includes(storedTeam)) {
                return storedTeam;
            }
            // Otherwise, fall back to the first team
            return teams[0];
        }

        // No teams available - return empty
        return "";
    }, [teams, storedTeam]);

    // Sync localStorage when the selected team changes
    useEffect(() => {
        if (selectedTeam && selectedTeam !== storedTeam) {
            // Only update if we have a valid team and it's different
            if (teams && teams.length > 0 && teams.includes(selectedTeam)) {
                setStoredTeam(selectedTeam);
            }
        }
    }, [selectedTeam, storedTeam, setStoredTeam, teams]);

    return [selectedTeam, setStoredTeam];
}
